import { useState, useCallback, useLayoutEffect, useRef } from 'react';
import { useAppSelector } from '../redux/hooks';
import { selectPlainSiteContent } from '../redux/slices/siteContentSlice';

/**
 * Configuration options for useNavSpacing hook
 */
interface UseNavSpacingOptions {
  /** CSS selector for the navigation element (default: 'nav') */
  navSelector?: string;
  /** Whether to include hero bar height in calculation (default: true) */
  includeHero?: boolean;
  /** Additional offset to add to the total spacing (default: 0) */
  extraOffset?: number;
}

/**
 * useNavSpacing
 * 
 * Calculates total vertical spacing needed for fixed/sticky positioning.
 * Dynamically measures navigation and optional hero bar heights, responding
 * to window resize and element size changes.
 * 
 * Performance optimizations:
 * - useLayoutEffect ensures synchronous measurement before browser paint
 * - RAF batching prevents multiple queued updates
 * - Debounced resize handler prevents excessive recalculations
 * - Equality check prevents redundant re-renders
 * - Explicit cleanup of observers and event listeners
 * 
 * @param options - Configuration options for customizing behavior
 * @returns {number} The calculated total spacing in pixels (nav + hero + offset)
 * 
 * @example
 * // Basic usage (includes hero bar)
 * const spacing = useNavSpacing();
 * 
 * @example
 * // Custom configuration
 * const spacing = useNavSpacing({ 
 *   navSelector: '.main-nav',
 *   includeHero: false,
 *   extraOffset: 20 
 * });
 */
export const useNavSpacing = (options: UseNavSpacingOptions = {}): number => {
  const { navSelector = 'nav', includeHero = true, extraOffset = 0 } = options;
  
  // Subscribe to site content changes for recalculation
  const siteContent = useAppSelector(selectPlainSiteContent);
  
  const [spacing, setSpacing] = useState(0);
  const navRef = useRef<HTMLElement | null>(null);
  const heroRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const frameIdRef = useRef<number | undefined>(undefined);
  const resizeTimeoutRef = useRef<number | undefined>(undefined);

  const calculateSpacing = useCallback(() => {
    // SSR safety guard
    if (typeof window === 'undefined') return;

    // Cancel any pending animation frame
    if (frameIdRef.current !== undefined) {
      cancelAnimationFrame(frameIdRef.current);
    }

    // Use requestAnimationFrame to prevent layout thrashing
    frameIdRef.current = requestAnimationFrame(() => {
      const nav = navRef.current ?? document.querySelector(navSelector);
      if (!nav) return;

      const navHeight = nav.offsetHeight || 0;
      const hero = includeHero
        ? heroRef.current ?? (nav.previousElementSibling as HTMLElement | null)
        : null;
      const heroHeight =
        hero && (hero.children.length > 0 || hero.textContent?.trim())
          ? hero.scrollHeight || 0
          : 0;

      const total = navHeight + heroHeight + extraOffset;
      
      // Prevent redundant re-renders
      setSpacing((prev) => (prev !== total ? total : prev));
    });
  }, [navSelector, includeHero, extraOffset]);

  useLayoutEffect(() => {
    // SSR safety guard
    if (typeof window === 'undefined') return;

    const initializeSpacing = () => {
      const nav = document.querySelector(navSelector);
      if (!nav) {
        // If nav not found, retry after short delay
        setTimeout(initializeSpacing, 50);
        return;
      }

      // Store element references
      navRef.current = nav as HTMLElement;
      heroRef.current = includeHero
        ? (nav.previousElementSibling as HTMLElement | null)
        : null;

      // Create ResizeObserver with RAF-batched callback
      observerRef.current = new ResizeObserver(() => {
        calculateSpacing();
      });

      // Observe elements
      observerRef.current.observe(navRef.current);
      if (heroRef.current) {
        observerRef.current.observe(heroRef.current);
      }

      // Calculate initial spacing
      calculateSpacing();
    };

    initializeSpacing();

    // Debounced resize handler (100ms debounce)
    const handleResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = window.setTimeout(calculateSpacing, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      // Cleanup observer
      if (observerRef.current) {
        if (navRef.current) {
          observerRef.current.unobserve(navRef.current);
        }
        if (heroRef.current) {
          observerRef.current.unobserve(heroRef.current);
        }
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      
      // Cleanup resize listener
      window.removeEventListener('resize', handleResize);
      
      // Cleanup pending timers and frames
      if (resizeTimeoutRef.current !== undefined) {
        clearTimeout(resizeTimeoutRef.current);
      }
      if (frameIdRef.current !== undefined) {
        cancelAnimationFrame(frameIdRef.current);
      }
    };
  }, [calculateSpacing, navSelector, includeHero, siteContent]);

  return spacing;
};
